/**
 * AI CONTEXT: mobile-first - User dashboard with purchases, giveaways, and subscription
 * Journal: J-20251128-01
 * 
 * This page provides:
 * - Overview of user profile and entries
 * - Purchase history and subscription status
 * - Quick navigation to core flows
 * 
 * Architecture decisions:
 * - Use contexts: Auth, Giveaway, Subscription
 * - Guard unauthenticated access and link to login
 * - Keep formatting/metrics consistent with dark theme
 * 
 * Future considerations:
 * - Add server-driven activity feed
 * - Provide downloadable receipts
 * - Integrate notifications and settings
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGiveaway } from '../contexts/GiveawayContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import {
  User, 
  ShoppingBag, 
  Gift, 
  CreditCard, 
  TrendingUp,
  Crown,
  LogOut,
  CheckCircle,
  Bell,
  Users,
  Crosshair
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { t } from '../i18n';
import { authorizedFetch, isApiConfigured, getMyPurchases, getMyReferralCode, getMyProfile, updateMyProfile, getMyReferrals } from '../services/apiClient';
import authService from '../services/auth';
import toast from 'react-hot-toast';
import { ensureDate, formatDateTime } from '../utils/date';

const Dashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const { giveaways, getUserEntries, getUserCredits } = useGiveaway();
  const { getUserSubscription, isUserSubscribed, getBonusEntries, cancelSubscription, refreshSubscription, loading: subLoading } = useSubscription();
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'overview';
  });
  
  // Handle verification completion
  const [showVerificationSuccess, setShowVerificationSuccess] = useState(false);
  const [serverEntries, setServerEntries] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [serverSubscription, setServerSubscription] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [profile, setProfile] = useState({ shippingAddress: {} });
  const [savingProfile, setSavingProfile] = useState(false);
  const [showShippingAddressNotification, setShowShippingAddressNotification] = useState(false);

  // Prevent repeated backend checks while on the subscription tab
  const hasRefreshedSubscriptionOnce = useRef(false);
  // Prevent repeated dashboard data loads (entries, purchases, subscription, profile)
  const hasLoadedDashboardDataOnce = useRef(false);
  // Stable user identifier for effect dependencies
  const userId = user?.email || user?.id;

  // Reset dashboard data guard when user changes
  useEffect(() => {
    hasLoadedDashboardDataOnce.current = false;
  }, [userId]);

  // AI CONTEXT: data-cleanup - Clear stale localStorage entries for active entries
  // Reasoning: Automatically clean up old localStorage entries that might cause incorrect active entry counts
  // Constraints: Must only clear entries that are no longer valid (expired giveaways)
  // Future: Consider implementing server-side entry validation to avoid client-side cleanup
  useEffect(() => {
    if (!userId) return;
    
    // Clear stale localStorage entries that might cause incorrect active entry counts
    const clearStaleEntries = () => {
      try {
        const localEntries = JSON.parse(localStorage.getItem(`tolbert_user_entries_${userId}`) || '[]');
        if (Array.isArray(localEntries) && localEntries.length > 0) {
          console.log('🔍 Checking for stale localStorage entries:', localEntries);
          
          // Filter out entries for giveaways that no longer exist or are expired
          const currentGiveawayIds = new Set(giveaways.map(g => g.id));
          const now = new Date();
          
          const validEntries = localEntries.filter(entry => {
            // Remove entries for giveaways that don't exist anymore
            if (!currentGiveawayIds.has(entry.giveawayId)) {
              console.log(`🔍 Removing entry for non-existent giveaway: ${entry.giveawayId}`);
              return false;
            }
            
            // Remove entries for expired giveaways
            const giveaway = giveaways.find(g => g.id === entry.giveawayId);
            if (giveaway && giveaway.endDate) {
              const endDate = new Date(giveaway.endDate);
              if (!Number.isNaN(endDate.getTime()) && endDate < now) {
                console.log(`🔍 Removing entry for expired giveaway: ${entry.giveawayId} (ended: ${giveaway.endDate})`);
                return false;
              }
            }
            
            return true;
          });
          
          // Update localStorage with only valid entries
          if (validEntries.length !== localEntries.length) {
            console.log(`🔍 Cleaned up ${localEntries.length - validEntries.length} stale entries`);
            if (validEntries.length === 0) {
              localStorage.removeItem(`tolbert_user_entries_${userId}`);
            } else {
              localStorage.setItem(`tolbert_user_entries_${userId}`, JSON.stringify(validEntries));
            }
          }
        }
      } catch (error) {
        console.warn('Failed to clear stale localStorage entries:', error);
      }
    };
    
    // Run cleanup when giveaways are loaded
    if (giveaways.length > 0) {
      clearStaleEntries();
    }
  }, [userId, giveaways]);

  // Credits now come from GiveawayContext (kept in sync with server on changes)

  // AI CONTEXT: data-fetching - Load server-persisted entries and handle Stripe redirects
  // Reasoning: Fetch user data from server when API is configured and handle post-payment redirects
  // Constraints: Must handle API unavailability gracefully and clean up URL parameters
  // Future: Consider implementing optimistic updates and better error handling
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      if (!isApiConfigured()) return;
      
      // Ensure authentication session is ready before making API calls
      // This is especially important after registration with autoSignIn
      let isAuthenticated = false;
      try {
        const authStatus = await authService.checkAuthStatus();
        isAuthenticated = authStatus.isAuthenticated;
        if (!isAuthenticated) {
          console.log('User not authenticated yet, waiting...');
          // Wait a bit more and try again
          await new Promise(resolve => setTimeout(resolve, 2000));
          const retryAuthStatus = await authService.checkAuthStatus();
          isAuthenticated = retryAuthStatus.isAuthenticated;
          if (!isAuthenticated) {
            console.warn('Authentication still not ready after retry');
            return;
          }
        }
      } catch (error) {
        console.warn('Auth status check failed:', error);
        // Don't proceed with API calls if we can't verify authentication
        return;
      }
      
      if (cancelled) return;
      
      // Handle verification completion
      const params = new URLSearchParams(window.location.search);
      const verificationStatus = params.get('verification');
      if (verificationStatus === 'complete') {
        setShowVerificationSuccess(true);
        // Clean up URL parameters
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('verification');
        window.history.replaceState({}, '', newUrl.toString());
        // Show success message for 5 seconds
        setTimeout(() => setShowVerificationSuccess(false), 5000);
      }
      
      // If redirected back from Stripe subscription, verify and refresh state
      try {
        const status = params.get('status');
        const sid = params.get('session_id');
        if (status === 'success' && sid) {
          // Verify the checkout session
          await authorizedFetch(`/payments/verify-session?session_id=${encodeURIComponent(sid)}`, { method: 'GET' });
          // Refresh subscription data after successful verification
          const refreshedSub = await refreshSubscription();
          if (!cancelled && refreshedSub) {
            setServerSubscription(refreshedSub);
          }
          // Set active tab to subscription if we have a successful redirect
          setActiveTab('subscription');
          // Clean up URL parameters
          const newUrl = new URL(window.location);
          newUrl.searchParams.delete('status');
          newUrl.searchParams.delete('session_id');
          window.history.replaceState({}, '', newUrl.toString());
        }
      } catch (error) {
        console.warn('Failed to verify checkout session:', error);
      }
      
      // Only make API calls if user is authenticated
      if (!isAuthenticated) {
        console.warn('Skipping API calls - user not authenticated');
        return;
      }
      
      // One-time guard to prevent repeated backend calls while on dashboard
      if (hasLoadedDashboardDataOnce.current) {
        console.log('Skipping repeated dashboard data load');
        return;
      }
      hasLoadedDashboardDataOnce.current = true;

      try {
        const data = await authorizedFetch('/my/entries', { method: 'GET' });
        if (!cancelled && Array.isArray(data)) setServerEntries(data);
      } catch (error) {
        if (error.message.includes('Authentication required')) {
          console.warn('Authentication required for entries API');
        } else {
          console.warn('Failed to load entries:', error);
        }
        // ignore; fallback to context overlay entries
      }
      try {
        const p = await getMyPurchases();
        console.log('🔍 Purchases API response:', p);
        if (!cancelled && Array.isArray(p)) {
          setPurchases(p);
          console.log('🔍 Purchases loaded successfully:', p.length, 'purchases');
        } else {
          console.log('🔍 Purchases API returned non-array or empty response:', p);
        }
      } catch (error) {
        if (error.message.includes('Authentication required')) {
          console.warn('Authentication required for purchases API');
        } else {
          console.warn('Failed to load purchases:', error);
        }
      }
      try {
        const sub = await authorizedFetch('/my/subscription', { method: 'GET' });
        if (!cancelled) {
          // Handle empty object response (no subscription)
          if (sub && Object.keys(sub).length === 0) {
            setServerSubscription(null);
          } else {
            setServerSubscription(sub || null);
          }
        }
      } catch (error) {
        if (error.message.includes('Authentication required')) {
          console.warn('Authentication required for subscription API');
        } else {
          console.warn('Failed to load subscription:', error);
        }
      }
      try {
        const rc = await getMyReferralCode();
        if (!cancelled && rc?.code) setReferralCode(rc.code);
      } catch (error) {
        if (error.message.includes('Authentication required')) {
          console.warn('Authentication required for referral code API');
        } else {
          console.warn('Failed to load referral code:', error);
        }
      }
      try {
        const refs = await getMyReferrals();
        if (!cancelled && refs?.referrals) setReferrals(refs.referrals);
      } catch (error) {
        if (error.message.includes('Authentication required')) {
          console.warn('Authentication required for referrals API');
        } else {
          console.warn('Failed to load referrals:', error);
        }
      }
      try {
        const prof = await getMyProfile();
        if (!cancelled && prof) {
          // Ensure shippingAddress is always an object, even if null/undefined from server
          const updatedProfile = {
            ...prof,
            shippingAddress: prof.shippingAddress || {}
          };
          setProfile(updatedProfile);
          
          // Check if shipping address is complete for giveaway eligibility
          const shippingAddress = updatedProfile.shippingAddress || {};
          const requiredFields = ['line1', 'city', 'state', 'postalCode'];
          const missingFields = requiredFields.filter(field => {
            const value = shippingAddress[field];
            return !value || (typeof value === 'string' && value.trim() === '');
          });
          
          // Show notification if shipping address is incomplete
          if (missingFields.length > 0) {
            console.log('⚠️ Shipping address incomplete - missing fields:', missingFields);
            setShowShippingAddressNotification(true);
          } else {
            console.log('✅ Shipping address complete - user eligible for giveaways');
            setShowShippingAddressNotification(false);
          }
        }
      } catch (error) {
        if (error.message.includes('Authentication required')) {
          console.warn('Authentication required for profile API');
        } else {
          console.warn('Failed to load profile:', error);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId, refreshSubscription]);

  // AI CONTEXT: subscription-refresh - Run once per visit to subscription tab
  // Reasoning: Avoid repeated backend calls while user remains on the subscription tab
  // Behavior: Triggers once when entering the tab; resets when leaving the tab
  useEffect(() => {
    let cancelled = false;
    if (!userId || !isApiConfigured()) return;
    
    if (activeTab !== 'subscription') {
      // Reset guard when leaving the tab so next visit can refresh once again
      hasRefreshedSubscriptionOnce.current = false;
      return;
    }
    
    if (hasRefreshedSubscriptionOnce.current) return;
    hasRefreshedSubscriptionOnce.current = true;
    
    (async () => {
      try {
        console.log('One-time subscription refresh on entering tab...');
        const refreshedSub = await refreshSubscription();
        if (cancelled) return;
        if (refreshedSub) {
          setServerSubscription(refreshedSub);
          console.log('Active Stripe subscription found:', refreshedSub);
          return;
        }
        
        console.log('No active Stripe subscription found - checking purchase history...');
        // If no active Stripe subscription, check if we have recent subscription purchases (local only)
        if (purchases && purchases.length > 0) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const recentSubscriptionPurchases = purchases.filter(purchase => {
            const purchaseDate = new Date(purchase.created || purchase.date);
            const isSubscription = purchase.name?.toLowerCase().includes('subscription') ||
                                  purchase.name?.toLowerCase().includes('membership') ||
                                  purchase.tier?.toLowerCase().includes('subscription') ||
                                  purchase.tier?.toLowerCase().includes('membership');
            return isSubscription && purchaseDate >= thirtyDaysAgo;
          });
          if (recentSubscriptionPurchases.length > 0) {
            const mostRecent = recentSubscriptionPurchases.sort((a, b) => new Date(b.created || b.date) - new Date(a.created || a.date))[0];
            console.log('Found recent subscription purchase:', mostRecent);
            // Keep serverSubscription null; UI will render from purchase-based fallback
          } else {
            console.log('No recent subscription purchases found');
          }
        }
        setServerSubscription(null);
      } catch (error) {
        console.warn('Failed to auto-refresh subscription data:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, activeTab, purchases, refreshSubscription]);

  if (!user) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center">
          <User className="h-16 w-16 text-theme-silver mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">{t('auth.pleaseSignIn')}</h2>
          <p className="text-theme-silver-light mb-4">{t('auth.signInRequired.dashboard')}</p>
          <Link to="/login" className="btn-primary">
            {t('auth.signin')}
          </Link>
        </div>
      </div>
    );
  }

  // AI CONTEXT: data-management - User data aggregation from multiple sources
  // Reasoning: Combine server and client-side data for comprehensive user dashboard
  // Constraints: Must handle data availability gracefully and provide fallbacks
  // Future: Consider implementing data synchronization and real-time updates
  
  const fallbackEntries = getUserEntries(userId);
  const ctxCredits = getUserCredits(userId);
  const subscription = serverSubscription || getUserSubscription(userId);
  const bonusEntries = getBonusEntries(userId); // TODO: wire to real backend later; currently used in UI
  
  // Check purchase history for recent subscription purchases (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const recentSubscriptionPurchases = purchases?.filter(purchase => {
    const purchaseDate = new Date((purchase.created || purchase.date) * 1000); // Fix: multiply by 1000 for Unix timestamp
    const isSubscription = purchase.name?.toLowerCase().includes('subscription') || 
                          purchase.name?.toLowerCase().includes('membership') ||
                          purchase.tier?.toLowerCase().includes('subscription') ||
                          purchase.tier?.toLowerCase().includes('membership');
    return isSubscription && purchaseDate >= thirtyDaysAgo;
  }) || [];
  
  // Determine subscription status: active Stripe subscription OR recent purchase
  const isSubscribed = Boolean(serverSubscription || isUserSubscribed(userId) || recentSubscriptionPurchases.length > 0);
  
  // Use most recent subscription purchase if no active Stripe subscription
  const subscriptionFromPurchase = recentSubscriptionPurchases.length > 0 
    ? recentSubscriptionPurchases.sort((a, b) => new Date((b.created || b.date) * 1000) - new Date((a.created || a.date) * 1000))[0]
    : null;
  
  // Debug logging for subscription detection
  console.log('🔍 Subscription Detection Debug:', {
    serverSubscription: !!serverSubscription,
    isUserSubscribed: isUserSubscribed(userId),
    recentSubscriptionPurchases: recentSubscriptionPurchases.length,
    subscriptionFromPurchase: !!subscriptionFromPurchase,
    isSubscribed,
    purchasesCount: purchases?.length || 0,
    thirtyDaysAgo: thirtyDaysAgo.toISOString()
  });
  
  // Detailed purchase analysis
  if (purchases && purchases.length > 0) {
    console.log('🔍 All purchases found:', purchases.map(p => ({
      name: p.name,
      tier: p.tier,
      date: p.created || p.date,
      amount: p.amount,
      purchaseDate: new Date((p.created || p.date) * 1000), // Fix: multiply by 1000 for Unix timestamp
      isWithin30Days: new Date((p.created || p.date) * 1000) >= thirtyDaysAgo, // Fix: multiply by 1000
      nameCheck: {
        hasSubscription: p.name?.toLowerCase().includes('subscription'),
        hasMembership: p.name?.toLowerCase().includes('membership')
      },
      tierCheck: {
        hasSubscription: p.tier?.toLowerCase().includes('subscription'),
        hasMembership: p.tier?.toLowerCase().includes('membership')
      }
    })));
  } else {
    console.log('🔍 No purchases found in purchases array');
  }
  
  if (recentSubscriptionPurchases.length > 0) {
    console.log('🔍 Recent subscription purchases found:', recentSubscriptionPurchases.map(p => ({
      name: p.name,
      tier: p.tier,
      date: p.created || p.date,
      amount: p.amount
    })));
  }

  // AI CONTEXT: data-processing - Server entries with giveaway metadata enrichment
  // Reasoning: Enhance server entries with giveaway titles and tier information for better UX
  // Constraints: Must handle missing giveaway data gracefully
  // Future: Consider implementing server-side data enrichment to reduce client processing
  
  // Prefer server entries when API is configured to avoid stale local overlays
  const preferServerEntries = isApiConfigured();
  const baseEntries = preferServerEntries ? (serverEntries || []) : fallbackEntries;
  const userEntries = baseEntries.map(e => ({
    ...e,
    giveawayTitle: giveaways.find(g => g.id === e.giveawayId)?.title || `Giveaway ${e.giveawayId}`,
    giveawayTier: giveaways.find(g => g.id === e.giveawayId)?.tier || e.tier
  }));

  // Compute active giveaways by endDate (no endDate treated as active until manually ended)
  const nowForActive = new Date();
  const isGiveawayActiveByDate = (g) => {
    if (!g) return false;
    if (!g.endDate) return true;
    const d = new Date(g.endDate);
    return !Number.isNaN(d.getTime()) && d >= nowForActive;
  };
  const activeGiveawayIds = new Set(giveaways.filter(isGiveawayActiveByDate).map(g => g.id));
  const activeUserEntries = userEntries.filter(e => activeGiveawayIds.has(e.giveawayId));

  // Group entries by giveaway to show all chosen slot numbers per giveaway
  const entriesByGiveaway = userEntries.reduce((acc, e) => {
    const key = e.giveawayId || e.giveawayTitle;
    if (!acc[key]) {
      acc[key] = {
        giveawayId: e.giveawayId,
        giveawayTitle: e.giveawayTitle,
        giveawayTier: e.giveawayTier,
        slots: [],
        latestDate: e.date,
      };
    }
    acc[key].slots.push(e.slotNumber);
    // keep latest entry date for display ordering
    if (new Date(e.date) > new Date(acc[key].latestDate)) acc[key].latestDate = e.date;
    return acc;
  }, {});
  const groupedEntries = Object.values(entriesByGiveaway).sort((a, b) => new Date(b.latestDate) - new Date(a.latestDate));

  const tabs = [
  { id: 'overview', name: t('dashboard.tabs.overview'), icon: User },
  { id: 'profile', name: 'Profile', icon: User },
  { id: 'purchases', name: t('dashboard.tabs.purchases'), icon: ShoppingBag },
  { id: 'giveaways', name: t('dashboard.tabs.giveaways'), icon: Gift },
  { id: 'referrals', name: 'Referrals', icon: Users },
  { id: 'subscription', name: t('dashboard.tabs.subscription'), icon: CreditCard },
  { id: 'notifications', name: 'Notifications', icon: Bell },
  ];

  // AI CONTEXT: ux - Date formatting utility
  // Reasoning: Provide consistent date formatting across the dashboard for better user experience
  // Constraints: Must handle various date formats and invalid dates gracefully
  // Future: Consider adding timezone support and more formatting options
  
  const formatDate = (dateValue, options = {}) =>
    formatDateTime(
      typeof dateValue === 'number' ? dateValue * 1000 : dateValue,
      { includeTime: false, ...options }
    );

  // AI CONTEXT: business-logic - Age calculation for legal compliance
  // Reasoning: Calculate user age from birthdate for age verification and legal compliance
  // Constraints: Must handle invalid dates and provide accurate age calculation
  // Future: Consider adding age verification caching and server-side validation
  
  const computeAgeFromBirthdate = (birthdateStr) => {
    if (!birthdateStr) return undefined;
    const birth = ensureDate(birthdateStr);
    if (!birth) return undefined;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
    return age;
  };

  return (
    <div className="min-h-screen bg-theme-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-theme-silver mb-2">
                {t('dashboard.welcome').replace('{name}', user.name)}
              </h1>
              <p className="text-base sm:text-lg text-theme-silver-light">{t('dashboard.track')}</p>
            </div>
            <button
              onClick={logout}
              className="btn-secondary flex items-center space-x-2 text-sm sm:text-base py-2 px-3 sm:py-3 sm:px-6 w-full sm:w-auto justify-center"
            >
              <LogOut className="h-4 w-4" />
              <span>{t('auth.signout')}</span>
            </button>
          </div>
        </div>

        {/* Shipping Address Notification */}
        {showShippingAddressNotification && (
          <div className="mb-6 glass-card-dark bg-yellow-500/20 border-yellow-400/30 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                  <span className="text-yellow-900 text-sm font-bold">!</span>
                </div>
                <div>
                  <h3 className="font-semibold text-theme-silver">Shipping Address Required</h3>
                  <p className="text-sm text-theme-silver-light">
                    Please go to the Dashboard {'>'} Profile Tab and update your shipping address to be eligible for entering giveaways.
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveTab('profile')}
                  className="btn-primary text-sm px-3 py-1"
                >
                  Update Now
                </button>
                <button
                  onClick={() => setShowShippingAddressNotification(false)}
                  className="text-theme-silver-light hover:text-theme-silver text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verification Success Message */}
        {showVerificationSuccess && (
          <div className="mb-6 glass-card-dark bg-green-500/20 border-green-400/30 p-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-6 w-6 text-green-400" />
              <div>
                <h3 className="font-semibold text-theme-silver">Age Verification Complete!</h3>
                <p className="text-sm text-theme-silver-light">
                  Your age has been successfully verified. You can now make purchases and enter giveaways.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-custom-orange text-white'
                      : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="glass-card-dark p-6">
                <div className="flex items-center">
                  <div className="bg-custom-orange/20 rounded-lg p-3">
                    <Gift className="h-6 w-6 text-custom-orange" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-theme-silver-light">{t('dashboard.stats.activeEntries')}</p>
                    <p className="text-2xl font-bold text-theme-silver">{activeUserEntries.length}</p>
                  </div>
                </div>
              </div>

              <div className="glass-card-dark p-6">
                <div className="flex items-center">
                  <div className="bg-green-500/20 rounded-lg p-3">
                    <ShoppingBag className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-theme-silver-light">{t('dashboard.stats.purchases')}</p>
                    <p className="text-2xl font-bold text-theme-silver">{user.purchases?.length || 0}</p>
                  </div>
                </div>
              </div>

              {/* Credits Summary */}
              <div className="glass-card-dark p-6">
                <div className="flex items-center">
                  <div className="bg-yellow-500/20 rounded-lg p-3">
                    <TrendingUp className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-theme-silver-light">{t('dashboard.stats.credits')}</p>
                    <p className="text-2xl font-bold text-theme-silver">
                      {(ctxCredits?.bronzeCredits || 0) + (ctxCredits?.silverCredits || 0) + (ctxCredits?.goldCredits || 0) + (ctxCredits?.platinumCredits || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card-dark p-6">
                <div className="flex h-full items-center">
                  <div className="bg-purple-500/20 rounded-lg p-3">
                    <Crown className="h-6 w-6 text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-theme-silver-light">{t('dashboard.stats.subscription')}</p>
                    <p className="text-2xl font-bold text-theme-silver leading-none">
                      {isSubscribed ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
              </div>
              {/* Referral Code */}
              <div className="glass-card-dark p-6">
                <div className="flex items-center">
                  <div className="bg-blue-500/20 rounded-lg p-3">
                    <Gift className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-theme-silver-light">{t('dashboard.referral')}</p>
                    <p className="text-2xl font-bold text-theme-silver">{referralCode || '—'}</p>
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/acquisition"
              className="glass-card-dark p-6 hover:shadow-glow transition-all duration-300 block border border-custom-orange/30"
            >
              <div className="flex items-center">
                <div className="bg-custom-orange/20 rounded-lg p-3">
                  <Crosshair className="h-6 w-6 text-custom-orange" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-theme-silver-light">Can&apos;t find it? We&apos;ll source it.</p>
                  <p className="text-2xl font-bold text-theme-silver">Acquire a Firearm</p>
                </div>
              </div>
            </Link>

            {/* Credits Breakdown */}
              <div className="glass-card-dark p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-4">{t('dashboard.creditsBreakdown')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Bronze', value: ctxCredits?.bronzeCredits || 0 },
                  { label: 'Silver', value: ctxCredits?.silverCredits || 0 },
                  { label: 'Gold', value: ctxCredits?.goldCredits || 0 },
                  { label: 'Platinum', value: ctxCredits?.platinumCredits || 0 },
                ].map((c) => (
                  <div key={c.label} className="text-center">
                    <div className={`tier-badge tier-${c.label.toLowerCase()} mb-2`}>{c.label}</div>
                    <p className="text-2xl font-bold text-theme-silver">{c.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity - Admin Only */}
            {isAdmin && (
              <div className="glass-card-dark p-6">
                <h3 className="text-lg font-semibold text-theme-silver mb-4">{t('dashboard.recentActivity')}</h3>
                <div className="space-y-4">
                  {user.purchases?.slice(0, 3).map((purchase) => (
                    <div key={purchase.id} className="flex items-center justify-between p-4 bg-glass-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <ShoppingBag className="h-5 w-5 text-custom-orange" />
                        <div>
                          <p className="font-medium text-theme-silver">Purchase completed</p>
                          <p className="text-sm text-theme-silver-light">${purchase.amount} • {purchase.entries} entries</p>
                        </div>
                      </div>
                      <span className="text-sm text-theme-silver-light">{formatDate(purchase.created || purchase.date)}</span>
                    </div>
                  ))}
                  
                  {groupedEntries.slice(0, 3).map((g, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-glass-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Gift className="h-5 w-5 text-custom-orange" />
                        <div>
                          <p className="font-medium text-theme-silver">Giveaway entry</p>
                          <p className="text-sm text-theme-silver-light">{g.giveawayTitle} • Slots {g.slots.sort((a,b)=>a-b).join(', ')}</p>
                        </div>
                      </div>
                      <span className="text-sm text-theme-silver-light">{formatDate(g.latestDate)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription Status */}
            {isSubscribed && subscription && (
              <div className="glass-card-dark bg-glass-copper/20 border-custom-orange/30 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Crown className="h-6 w-6 text-custom-orange" />
                  <h3 className="text-lg font-semibold text-theme-silver">Premium Subscription</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-theme-silver-light">Status</p>
                    <p className="font-semibold text-theme-silver capitalize">{subscription.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-theme-silver-light">Next Billing</p>
                    <p className="font-semibold text-theme-silver">{formatDate(subscription.nextBillingDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-theme-silver-light">Bonus Entries</p>
                    <p className="font-semibold text-custom-orange">{bonusEntries}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Purchase History Tab */}
        {activeTab === 'purchases' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-theme-silver">Purchase History</h2>
            
            <div className="glass-card-dark p-6">
              {(purchases && purchases.length > 0) ? (
                <div className="space-y-4">
                  {purchases
                    .sort((a, b) => {
                      // Sort by date with most recent first
                      const dateA = new Date(a.created || a.date);
                      const dateB = new Date(b.created || b.date);
                      return dateB - dateA;
                    })
                    .map((purchase) => (
                    <div key={purchase.id || purchase.purchaseId} className="flex items-center justify-between p-4 border border-theme-silver/20 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-glass-white/10 rounded-lg flex items-center justify-center">
                          <ShoppingBag className="h-6 w-6 text-theme-silver" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-theme-silver">Purchase #{purchase.id || purchase.purchaseId}</h3>
                          {purchase.entries !== undefined && (
                            <p className="text-sm text-theme-silver-light">{purchase.entries} giveaway entries</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-theme-silver">${purchase.amount?.toFixed ? purchase.amount.toFixed(2) : purchase.amount}</p>
                        <p className="text-sm text-theme-silver-light">{formatDate(purchase.created || purchase.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingBag className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">{t('dashboard.noPurchases')}</h3>
                  <p className="text-theme-silver-light mb-4">{t('dashboard.startShopping')}</p>
                  <Link to="/store" className="btn-primary">
                    Shop Now
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-theme-silver">Profile</h2>
            <div className="glass-card-dark p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-theme-silver-light">Full Name</p>
                  <p className="text-lg font-semibold text-theme-silver">{user.name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-theme-silver-light">Email</p>
                  <p className="text-lg font-semibold text-theme-silver">{user.email || '—'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-theme-silver-light">Phone</p>
                  <p className="text-lg font-semibold text-theme-silver">{user.phone || '—'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-theme-silver-light">Birth Date</p>
                  <p className="text-lg font-semibold text-theme-silver">{user.birthDate ? formatDate(user.birthDate) : '—'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-theme-silver-light">Age</p>
                  <p className="text-lg font-semibold text-theme-silver">{(user.age || user.age === 0) ? user.age : (computeAgeFromBirthdate(user.birthDate) ?? '—')}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-theme-silver-light">Member Since</p>
                  <p className="text-lg font-semibold text-theme-silver">{user.joinDate ? formatDate(user.joinDate) : '—'}</p>
                </div>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-theme-silver mb-4">Shipping Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">Address Line 1</label>
                    <input
                      type="text"
                      value={profile?.shippingAddress?.line1 || ''}
                      onChange={e => setProfile(prev => ({ 
                        ...prev, 
                        shippingAddress: { 
                          ...(prev?.shippingAddress || {}), 
                          line1: e.target.value 
                        } 
                      }))}
                      className="w-full glass-input px-4 py-3"
                      placeholder="123 Main St"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">Address Line 2</label>
                    <input
                      type="text"
                      value={profile?.shippingAddress?.line2 || ''}
                      onChange={e => setProfile(prev => ({ 
                        ...prev, 
                        shippingAddress: { 
                          ...(prev?.shippingAddress || {}), 
                          line2: e.target.value 
                        } 
                      }))}
                      className="w-full glass-input px-4 py-3"
                      placeholder="Apt, suite, etc. (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">City</label>
                    <input
                      type="text"
                      value={profile?.shippingAddress?.city || ''}
                      onChange={e => setProfile(prev => ({ 
                        ...prev, 
                        shippingAddress: { 
                          ...(prev?.shippingAddress || {}), 
                          city: e.target.value 
                        } 
                      }))}
                      className="w-full glass-input px-4 py-3"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">State</label>
                    <input
                      type="text"
                      value={profile?.shippingAddress?.state || ''}
                      onChange={e => setProfile(prev => ({ 
                        ...prev, 
                        shippingAddress: { 
                          ...(prev?.shippingAddress || {}), 
                          state: e.target.value 
                        } 
                      }))}
                      className="w-full glass-input px-4 py-3"
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">Postal Code</label>
                    <input
                      type="text"
                      value={profile?.shippingAddress?.postalCode || ''}
                      onChange={e => setProfile(prev => ({ 
                        ...prev, 
                        shippingAddress: { 
                          ...(prev?.shippingAddress || {}), 
                          postalCode: e.target.value 
                        } 
                      }))}
                      className="w-full glass-input px-4 py-3"
                      placeholder="ZIP / Postal code"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-theme-silver-light mb-2">Country</label>
                    <input
                      type="text"
                      value={profile?.shippingAddress?.country || 'US'}
                      onChange={e => setProfile(prev => ({ 
                        ...prev, 
                        shippingAddress: { 
                          ...(prev?.shippingAddress || {}), 
                          country: e.target.value 
                        } 
                      }))}
                      className="w-full glass-input px-4 py-3"
                      placeholder="Country"
                    />
                  </div>
                </div>
                <div className="mt-4 text-right">
                  <button
                    className="btn-primary"
                    disabled={savingProfile}
                    onClick={async () => {
                      try {
                        setSavingProfile(true);
                        
                        // Get current shipping address from state
                        const currentAddress = profile?.shippingAddress || {};
                        
                        // Debug logging
                        console.log('🔍 DEBUG: Current profile state:', profile);
                        console.log('🔍 DEBUG: Current shipping address:', currentAddress);
                        console.log('🔍 DEBUG: Required fields check:', {
                          line1: !!currentAddress.line1,
                          city: !!currentAddress.city,
                          state: !!currentAddress.state,
                          postalCode: !!currentAddress.postalCode
                        });
                        
                        // Validate required fields - check for empty strings and undefined values
                        const requiredFields = ['line1', 'city', 'state', 'postalCode'];
                        const missingFields = requiredFields.filter(field => {
                          const value = currentAddress[field];
                          return !value || (typeof value === 'string' && value.trim() === '');
                        });
                        
                        if (missingFields.length > 0) {
                          console.log('❌ DEBUG: Validation failed - missing required fields:', missingFields);
                          toast.error(`Please fill in all required address fields: ${missingFields.join(', ')}`);
                          return;
                        }
                        
                        // Clean up empty optional fields
                        const shippingAddress = {
                          line1: currentAddress.line1.trim(),
                          city: currentAddress.city.trim(),
                          state: currentAddress.state.trim(),
                          postalCode: currentAddress.postalCode.trim(),
                          country: currentAddress.country?.trim() || 'US'
                        };
                        
                        // Add line2 only if it's not empty
                        if (currentAddress.line2?.trim()) {
                          shippingAddress.line2 = currentAddress.line2.trim();
                        }
                        
                        console.log('✅ DEBUG: Validation passed - saving shipping address:', shippingAddress);
                        const saved = await updateMyProfile({ shippingAddress });
                        setProfile(prev => ({ ...prev, ...saved }));
                        toast.success('Shipping address saved successfully');
                        
                        // Hide the shipping address notification since it's now complete
                        setShowShippingAddressNotification(false);
                        
                        // Clear the shipping address cache to ensure other pages get updated
                        try {
                          localStorage.removeItem('tolbert_shipping_address_status');
                          console.log('✅ Cleared shipping address cache for other pages');
                          
                          // Dispatch a custom event to notify other components
                          window.dispatchEvent(new CustomEvent('shippingAddressUpdated', {
                            detail: { status: 'complete' }
                          }));
                        } catch (error) {
                          console.warn('Failed to clear shipping address cache:', error);
                        }
                      } catch (error) {
                        console.error('❌ DEBUG: Error saving shipping address:', error);
                        toast.error('Failed to save shipping address. Please try again.');
                      } finally {
                        setSavingProfile(false);
                      }
                    }}
                  >
                    {savingProfile ? 'Saving…' : 'Save Address'}
                  </button>
                </div>
              </div>
              <p className="mt-6 text-xs text-theme-silver-light">Profile data is sourced from your account (Cognito) and shipping address is stored in DynamoDB.</p>
            </div>

            {/* Notification Settings Section */}
            <div className="glass-card-dark p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Bell className="h-6 w-6 text-custom-orange" />
                <h3 className="text-xl font-semibold text-theme-silver">Notification Settings</h3>
              </div>
              <p className="text-theme-silver-light mb-6">
                Manage how you receive notifications for giveaways, orders, and updates.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-theme-dark/30 rounded-lg">
                  <div>
                    <h4 className="text-theme-silver font-medium">SMS Notifications</h4>
                    <p className="text-theme-silver-light text-sm">
                      Receive text messages for important updates and giveaways
                    </p>
                  </div>
                  <Link
                    to="/notifications"
                    className="btn-secondary text-sm px-4 py-2"
                  >
                    Manage
                  </Link>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-theme-dark/30 rounded-lg">
                  <div>
                    <h4 className="text-theme-silver font-medium">Email Notifications</h4>
                    <p className="text-theme-silver-light text-sm">
                      Get detailed notifications and updates via email
                    </p>
                  </div>
                  <Link
                    to="/notifications"
                    className="btn-secondary text-sm px-4 py-2"
                  >
                    Manage
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* My Giveaways Tab */}
        {activeTab === 'giveaways' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-theme-silver">My Giveaway Entries</h2>
            
            <div className="glass-card-dark p-6">
              {groupedEntries.length > 0 ? (
                <div className="space-y-4">
                  {groupedEntries.map((g, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-theme-silver/20 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-glass-white/10 rounded-lg flex items-center justify-center">
                          <Gift className="h-6 w-6 text-theme-silver" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-theme-silver">{g.giveawayTitle}</h3>
                          <p className="text-sm text-theme-silver-light">Slots {g.slots.sort((a,b)=>a-b).join(', ')} • {g.giveawayTier} Tier</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-theme-silver-light">{formatDate(g.latestDate)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">{t('dashboard.noEntries')}</h3>
                  <p className="text-theme-silver-light mb-4">{t('dashboard.startShoppingOrEnter')}</p>
                  <div className="flex space-x-4 justify-center">
                    <Link to="/store" className="btn-primary">
                      Shop Now
                    </Link>
                    <Link to="/giveaways" className="btn-secondary">
                      View Giveaways
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-theme-silver">My Referrals</h2>

            {/* Referred By (shown when this user was referred by someone) */}
            {profile?.referredBy && (
              <div className="glass-card-dark p-6 border-2 border-theme-silver/20">
                <div className="flex items-center gap-3">
                  <Users className="h-8 w-8 text-theme-silver-light" />
                  <div>
                    <h3 className="text-lg font-semibold text-theme-silver">You were referred</h3>
                    <p className="text-sm text-theme-silver-light">
                      Referred by {profile.referredBy.referrerEmail || '—'}
                      {profile.referredBy.referralCode && ` (code ${profile.referredBy.referralCode})`}
                      {profile.referredBy.referredAt && (() => {
                        const formatted = formatDate(profile.referredBy.referredAt);
                        return formatted && formatted !== 'TBD' ? ` • ${formatted}` : null;
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Referral Code Card */}
            {referralCode && (
              <div className="glass-card-dark p-6 border-2 border-custom-orange/30">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-theme-silver mb-1">Your Referral Code</h3>
                    <p className="text-sm text-theme-silver-light">Share this code with friends to earn credits!</p>
                  </div>
                  <Users className="h-8 w-8 text-custom-orange" />
                </div>
                
                {/* Referral Code Display */}
                <div className="bg-theme-black/50 rounded-lg p-4 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold text-custom-orange tracking-wider">{referralCode}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(referralCode);
                        toast.success('Referral code copied to clipboard!');
                      }}
                      className="btn-secondary text-sm"
                    >
                      Copy Code
                    </button>
                  </div>
                </div>

                {/* Shareable Link Display */}
                <div className="bg-theme-black/50 rounded-lg p-4 mb-4">
                  <p className="text-xs text-theme-silver-light mb-2 font-semibold uppercase tracking-wide">Shareable Link</p>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 overflow-hidden">
                      <span className="text-sm text-theme-silver font-mono break-all">
                        https://tolberttactical.com/share/{referralCode}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        const shareUrl = `https://tolberttactical.com/share/${referralCode}`;
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Referral link copied to clipboard!');
                      }}
                      className="btn-primary text-sm whitespace-nowrap"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>

                <div className="text-sm text-theme-silver-light space-y-1">
                  <p>• Share your code or link with friends during registration</p>
                  <p>• You'll earn 1 Bronze credit when they register</p>
                  <p>• Credits are automatically added to your account</p>
                  <p className="text-xs text-theme-silver-light/70 mt-2">
                    💡 Tip: Use the shareable link for easy one-click referrals!
                  </p>
                </div>
              </div>
            )}

            {/* Referrals List */}
            <div className="glass-card-dark p-6">
              <h3 className="text-xl font-semibold text-theme-silver mb-4">
                People Who Used Your Code ({referrals.length})
              </h3>
              
              {referrals.length > 0 ? (
                <div className="space-y-3">
                  {referrals.map((ref, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-theme-silver/20 rounded-lg hover:border-custom-orange/30 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-glass-white/10 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-theme-silver" />
                        </div>
                        <div>
                          <p className="font-medium text-theme-silver">{ref.email}</p>
                          <p className="text-xs text-theme-silver-light">
                            Registered {formatDate(ref.registeredAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {ref.creditIssued ? (
                          <div className="flex items-center space-x-1 text-green-400">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">+1 Credit</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1 text-theme-silver-light">
                            <span className="text-sm">Processing...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-theme-silver mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">No Referrals Yet</h3>
                  <p className="text-theme-silver-light mb-4">
                    Share your referral code or link with friends to earn credits when they register!
                  </p>
                  {referralCode && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        onClick={() => {
                          const shareUrl = `https://tolberttactical.com/share/${referralCode}`;
                          navigator.clipboard.writeText(shareUrl);
                          toast.success('Referral link copied! Share it with your friends.');
                        }}
                        className="btn-primary"
                      >
                        Copy Referral Link
                      </button>
                      <button
                        onClick={() => {
                          const message = `Join Tolbert Tactical! Use my referral link to get started:\n\nhttps://tolberttactical.com/share/${referralCode}`;
                          navigator.clipboard.writeText(message);
                          toast.success('Referral message copied! Share it with your friends.');
                        }}
                        className="btn-secondary"
                      >
                        Copy Message
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-theme-silver">{t('dashboard.tabs.subscription')}</h2>
              <button
                onClick={async () => {
                  try {
                    console.log('Manual subscription refresh requested...');
                    
                    // First try to get active Stripe subscription
                    const refreshed = await refreshSubscription();
                    
                    if (refreshed) {
                      setServerSubscription(refreshed);
                      console.log('Manual refresh successful - Active Stripe subscription:', refreshed);
                      toast.success('Active subscription found and refreshed');
                    } else {
                      console.log('Manual refresh: No active Stripe subscription found');
                      
                      // Check purchase history for recent subscription purchases
                      if (purchases && purchases.length > 0) {
                        const thirtyDaysAgo = new Date();
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        
                        const recentSubscriptionPurchases = purchases.filter(purchase => {
                          const purchaseDate = new Date(purchase.created || purchase.date);
                          const isSubscription = purchase.name?.toLowerCase().includes('subscription') || 
                                                purchase.name?.toLowerCase().includes('membership') ||
                                                purchase.tier?.toLowerCase().includes('subscription') ||
                                                purchase.tier?.toLowerCase().includes('membership');
                          return isSubscription && purchaseDate >= thirtyDaysAgo;
                        });
                        
                        if (recentSubscriptionPurchases.length > 0) {
                          const mostRecent = recentSubscriptionPurchases.sort((a, b) => 
                            new Date(b.created || b.date) - new Date(a.created || a.date)
                          )[0];
                          
                          console.log('Manual refresh: Found recent subscription purchase:', mostRecent);
                          setServerSubscription(null); // Clear Stripe subscription
                          toast.success(`Found recent subscription purchase: ${mostRecent.name || mostRecent.tier}`);
                        } else {
                          console.log('Manual refresh: No recent subscription purchases found');
                          setServerSubscription(null);
                          toast.success('No active subscription found');
                        }
                      } else {
                        setServerSubscription(null);
                        toast.success('No active subscription found');
                      }
                    }
                  } catch (error) {
                    console.error('Manual refresh failed:', error);
                    toast.error('Failed to refresh subscription data. Please try again.');
                  }
                }}
                className="btn-secondary text-sm"
                disabled={subLoading}
              >
                {subLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            
            {subLoading ? (
              <div className="glass-card-dark p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-orange mx-auto mb-4"></div>
                <p className="text-theme-silver-light">Loading subscription data...</p>
              </div>
            ) : isSubscribed ? (
              // Show subscription info from either active Stripe subscription or recent purchase
              <div className="glass-card-dark p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <Crown className="h-8 w-8 text-custom-orange" />
                  <div>
                    <h3 className="text-xl font-semibold text-theme-silver">{t('dashboard.subscription.premium')}</h3>
                    <p className="text-theme-silver-light">
                      {subscription ? 'You have access to exclusive benefits' : 'Your subscription is active'}
                    </p>
                  </div>
                </div>
                
                {subscription ? (
                  // Active Stripe subscription
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Status</p>
                          <p className="text-lg font-semibold text-green-400 capitalize">{subscription.status}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Plan</p>
                          <p className="text-lg font-semibold text-theme-silver capitalize">{subscription.plan}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Amount</p>
                          <p className="text-lg font-semibold text-theme-silver">${subscription.amount}/month</p>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Next Billing Date</p>
                          <p className="text-lg font-semibold text-theme-silver">{formatDate(subscription.nextBillingDate)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Bonus Entries</p>
                          <p className="text-lg font-semibold text-custom-orange">{bonusEntries}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 text-right">
                      <button
                        className="btn-secondary"
                        disabled={subLoading}
                        onClick={() => cancelSubscription(subscription.subscriptionId)}
                      >
                        {subLoading ? 'Cancelling…' : 'Cancel Subscription'}
                      </button>
                    </div>
                  </>
                ) : subscriptionFromPurchase ? (
                  // Recent subscription purchase
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Status</p>
                          <p className="text-lg font-semibold text-green-400 capitalize">Active</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Plan</p>
                          <p className="text-lg font-semibold text-theme-silver capitalize">{subscriptionFromPurchase.name || subscriptionFromPurchase.tier}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Amount</p>
                          <p className="text-lg font-semibold text-theme-silver">${subscriptionFromPurchase.amount?.toFixed ? subscriptionFromPurchase.amount.toFixed(2) : subscriptionFromPurchase.amount}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Purchase Date</p>
                          <p className="text-lg font-semibold text-theme-silver">{formatDate(subscriptionFromPurchase.created || subscriptionFromPurchase.date)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Bonus Entries</p>
                          <p className="text-lg font-semibold text-custom-orange">{bonusEntries}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-theme-silver-light">Entries Included</p>
                          <p className="text-lg font-semibold text-theme-silver">{subscriptionFromPurchase.entries || 1}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-400/20 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <p className="text-sm text-theme-silver-light">
                          This subscription was purchased recently. Your benefits are active for this billing period.
                        </p>
                      </div>
                    </div>
                  </>
                ) : null}
                
                <div className="mt-6 pt-6 border-t border-theme-silver/20">
                  <h4 className="font-semibold text-theme-silver mb-3">Premium Benefits</h4>
                  <ul className="space-y-2 text-sm text-theme-silver-light">
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                      <span>48-hour early access to new giveaways</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                      <span>5 bonus entries per month</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                      <span>Priority customer support</span>
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="glass-card-dark p-6 text-center">
                <Crown className="h-16 w-16 text-theme-silver mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-theme-silver mb-2">{t('dashboard.noActiveSubscription')}</h3>
                <p className="text-theme-silver-light mb-6">{t('dashboard.upgradeBenefits')}</p>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:ring-offset-2 focus:ring-offset-theme-black shadow-lg hover:shadow-green-500/20"
                  onClick={() => {
                    // Lead to Store subscriptions view; checkout flow handles Stripe redirect
                    window.location.assign('/store?type=subscription');
                  }}
                >
                  Browse Subscriptions
                </button>
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-theme-silver">Notifications</h2>
            </div>
            
            <div className="glass-card-dark p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Bell className="h-8 w-8 text-custom-orange" />
                <div>
                  <h3 className="text-xl font-semibold text-theme-silver">Notification Preferences</h3>
                  <p className="text-theme-silver-light">
                    Manage how you receive notifications for giveaways, orders, and updates.
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* SMS Notifications */}
                  <div className="p-4 bg-theme-dark/30 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <Bell className="h-6 w-6 text-custom-orange" />
                      <h4 className="text-theme-silver font-medium">SMS Notifications</h4>
                    </div>
                    <p className="text-theme-silver-light text-sm mb-4">
                      Receive text messages for important updates and giveaways
                    </p>
                    <Link
                      to="/notifications"
                      className="btn-secondary text-sm px-4 py-2 w-full text-center"
                    >
                      Manage SMS
                    </Link>
                  </div>
                  
                  {/* Email Notifications */}
                  <div className="p-4 bg-theme-dark/30 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <Bell className="h-6 w-6 text-custom-orange" />
                      <h4 className="text-theme-silver font-medium">Email Notifications</h4>
                    </div>
                    <p className="text-theme-silver-light text-sm mb-4">
                      Get detailed notifications and updates via email
                    </p>
                    <Link
                      to="/notifications"
                      className="btn-secondary text-sm px-4 py-2 w-full text-center"
                    >
                      Manage Email
                    </Link>
                  </div>
                </div>
                
                {/* Quick Actions */}
                <div className="border-t border-theme-dark pt-6">
                  <h4 className="text-theme-silver font-medium mb-4">Quick Actions</h4>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/notifications"
                      className="btn-primary text-sm px-4 py-2"
                    >
                      View All Settings
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 